import { useDispatch, useSelector } from "react-redux";
import { axiosInstance } from "lib/axios";
import { DistributionListService } from "../services";
import { DistributionList, DistributionListDetail } from "../models";
import { setDistributionLists } from "../context/settingsSlice";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

const initialState = {
    distList: false
}

export function useDistributionLists() {
    const dispatch = useDispatch();
    const { distributionLists } = useSelector((state) => state.settings);
    const [loading, setLoading] = useState(false);
    const [editingList, setEditingList] = useState(null);

    const distListService = new DistributionListService(axiosInstance);

    function normalizeDistributionListRecord(record) {
        return {
            distributionListId: record?.distributionListId_ids ?? record?.distributionListId ?? record?.id,
            distributionName: record?.distributionName_chr ?? record?.distributionName ?? record?.name,
            generatedDistributionName: record?.generatedDistributionName_chr ?? record?.generatedDistributionName ?? record?.slug,
        };
    }

    function normalizeUserRecord(record) {
        const email = record?.emailId_chr ?? record?.normalizedEmailId_chr ?? record?.email ?? null;
        const userId = record?.userId_chr ?? record?.userId ?? null;
        
        if (!email && !userId) {
            return null;
        }
        
        const id = userId ?? email;
        const username = record?.username ?? (email ? email.split('@')[0] : null);
        return { id, userId, email, username, avatar: record?.avatar ?? null };
    }

    async function getDistributionLists() {
        try {
            setLoading(true);
            const distLists = await distListService.getDistributionLists();
            const rows = Array.isArray(distLists?.data) ? distLists.data : Array.isArray(distLists) ? distLists : [];
            const formattedLists = rows.map((list) => new DistributionList(normalizeDistributionListRecord(list)));
            dispatch(setDistributionLists(formattedLists));
        } catch (error) {
            console.error(error);
            toast.error("Failed to load distribution lists");
        } finally {
            setLoading(false);
        }
    }

    async function createDistributionList(distributionName) {
        try {
            setLoading(true);
            const { data } = await distListService.createDistributionList(distributionName);
            const newList = new DistributionList({
                distributionListId: data.distributionListId_ids,
                distributionName: data.distributionName_chr,
                generatedDistributionName: data.generatedDistributionName_chr
            });
            const updatedLists = [...distributionLists, newList];
            dispatch(setDistributionLists(updatedLists));
            toast.success("Distribution list created successfully");
            return newList;
        } catch (error) {
            console.error(error);
            toast.error("Failed to create distribution list");
            throw error;
        } finally {
            setLoading(false);
        }
    }

    async function updateDistributionListUsers(distributionListId, users) {
        try {
            setLoading(true);
            await distListService.updateDistributionListUsers(distributionListId, users);
            await getDistributionLists();
            toast.success("Distribution list updated successfully");
        } catch (error) {
            console.error(error);
            toast.error("Failed to update distribution list");
            throw error;
        } finally {
            setLoading(false);
        }
    }

    async function deleteDistributionList(distributionListId) {
        try {
            setLoading(true);
            await distListService.deleteDistributionList(distributionListId);
            const updatedLists = distributionLists.filter(list => list.id !== distributionListId);
            dispatch(setDistributionLists(updatedLists));
            toast.success("Distribution list deleted successfully");
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete distribution list");
            throw error;
        } finally {
            setLoading(false);
        }
    }

    async function deleteDistributionListUsers(distributionListId, userEmails) {
        try {
            setLoading(true);
            await distListService.deleteDistributionListUsers(distributionListId, userEmails);
            await getDistributionLists();
            toast.success("Users removed from distribution list successfully");
        } catch (error) {
            console.error(error);
            toast.error("Failed to remove users from distribution list");
            throw error;
        } finally {
            setLoading(false);
        }
    }

    async function getDistributionListDetails(distributionListId) {
        try {
            const details = await distListService.getDistributionListDetails(distributionListId);
            const dataArray = Array.isArray(details?.data) ? details.data : Array.isArray(details) ? details : details ? [details] : [];
            
            if (dataArray.length === 0) {
                return [];
            }
            
            const firstItem = dataArray[0];
            const distributionListDetail = new DistributionListDetail(firstItem);
            
            return distributionListDetail.users;
        } catch (error) {
            console.error(error);
            toast.error("Failed to load distribution list details");
            throw error;
        }
    }

    useEffect(() => {
        async function getData() {
            await getDistributionLists();
        }
        if(initialState.distList) {
            return;
        }
        initialState.distList = true;
        getData();
    }, [])

    return {
        distributionLists,
        loading,
        editingList,
        setEditingList,
        createDistributionList,
        updateDistributionListUsers,
        deleteDistributionList,
        deleteDistributionListUsers,
        getDistributionListDetails,
        getDistributionLists,
    }
}